import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, CalendarDays, Check, Ticket } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fDate, fTime } from "@/components/TicketSlide";

const SessionOption = ({ s, selected, onPick }) => {
  const full = s.available <= 0;
  return (
    <button type="button" disabled={full} onClick={() => onPick(s.id)} aria-pressed={selected} data-testid={`redeem-session-${s.id}`}
      className={`w-full flex items-center justify-between gap-3 rounded-xl border p-3 text-left transition-colors ${full ? "opacity-50 cursor-not-allowed" : selected ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:border-primary/60"}`}>
      <span className="flex items-center gap-2.5 min-w-0">
        <span className={`w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 ${selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"}`}>{selected && <Check className="w-3 h-3" />}</span>
        <span className="text-sm"><span className="font-medium capitalize">{fDate(s.starts_at)}</span> · {fTime(s.starts_at)} hrs</span>
      </span>
      <Badge variant="outline" className={full ? "" : "border-emerald-500/40 text-emerald-700 dark:text-emerald-400"}>{full ? "Sin cupos" : `${s.available} cupo${s.available === 1 ? "" : "s"}`}</Badge>
    </button>
  );
};

export function RedeemCreditDialog({ credit, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/credits/${credit.id}/options`).then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); onClose(); });
  }, [credit.id, onClose]);
  const chosen = data?.workshops.flatMap((w) => w.sessions.map((s) => ({ ...s, w }))).find((s) => s.id === pick);

  const redeem = async () => {
    setBusy(true);
    try {
      await api.post(`/credits/${credit.id}/redeem`, { session_id: pick });
      toast.success("¡Listo! Tomaste la sesión. Tu ticket de canje ya está en Mis Tickets.");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      api.get(`/credits/${credit.id}/options`).then((r) => setData(r.data)).catch(() => {});
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="redeem-dialog">
        <DialogHeader>
          <DialogTitle>Canjear mi crédito</DialogTitle>
          <DialogDescription>Elige una sesión con cupos de un taller de <strong>{credit.business_name}</strong>. El crédito se usa una sola vez: si luego no asistes, no se recupera.</DialogDescription>
        </DialogHeader>
        {!data ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-8" /> : data.workshops.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8" data-testid="redeem-empty">Por ahora este Maestro no tiene sesiones próximas disponibles. Vuelve a revisar más adelante.</p>
        ) : (
          <div className="space-y-5" data-testid="redeem-options">
            {data.workshops.map((w) => (
              <section key={w.workshop_id} className="space-y-2" data-testid={`redeem-workshop-${w.workshop_id}`}>
                <div>
                  <p className="font-semibold text-sm flex items-center gap-2">{w.workshop_name}{w.same && <Badge className="bg-sun text-[#3d2b00] hover:bg-sun text-[10px]">Mismo taller</Badge>}</p>
                  {(w.commune || w.address) && <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{[w.address, w.commune].filter(Boolean).join(", ")}</p>}
                </div>
                {w.sessions.map((s) => <SessionOption key={s.id} s={s} selected={pick === s.id} onPick={setPick} />)}
              </section>
            ))}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
          {chosen && <p className="text-xs text-muted-foreground sm:mr-auto flex items-center gap-1.5" data-testid="redeem-summary"><CalendarDays className="w-3.5 h-3.5" />{chosen.w.workshop_name} · {fDate(chosen.starts_at)} {fTime(chosen.starts_at)} hrs</p>}
          <Button onClick={redeem} disabled={!pick || busy} className="gap-1.5" data-testid="redeem-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}Tomar esta sesión
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
